import { useId, useRef, type FC, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import type {
  MarkdownOutlineHeading,
  MarkdownOutlineState,
} from "../../../components/codeMirror/extensions/markdownOutline.js";
import {
  NavigationRail,
  NavigationRailHeaderItem,
  NavigationRailItem,
  useNavigationRailItems,
} from "../../../components/navigationRail/navigationRail.js";
import { computeNavigationRailThumb } from "../../../components/navigationRail/navigationRailThumb.js";

// Indents a heading by its depth in the outline.
const DEPTH_INDENT_CLASSES = ["", "pl-3", "pl-6"];

interface InstructionTileRowProps {
  icon: ReactNode;
  title: ReactNode;
  /**
   * The outline of the tile's editor. The tile that was active last keeps
   * its own, so it can animate closed.
   */
  outline: MarkdownOutlineState | undefined;
  isActive: boolean;
  /** Shows the outline of the active row. */
  isExpanded: boolean;
  onPress: () => void;
  onHeadingPress: (lineNumber: number) => void;
}

/**
 * A tile in the Instructions tab's navigation. While it is active, the
 * outline of its markdown headings hangs below it.
 */
export const InstructionTileRow: FC<InstructionTileRowProps> = (props) => {
  const {
    icon,
    title,
    outline,
    isActive,
    isExpanded,
    onPress,
    onHeadingPress,
  } = props;
  const outlineId = useId();
  const headings = outline?.headings ?? [];
  const hasOutline = headings.length > 0;
  const hasToggle = isActive && hasOutline;
  const isOutlineOpen = hasToggle && isExpanded;

  return (
    <li>
      <NavigationRailHeaderItem
        icon={icon}
        title={title}
        isExpanded={hasToggle ? isOutlineOpen : undefined}
        hasRail={hasOutline}
        aria-controls={hasToggle ? outlineId : undefined}
        aria-current={isActive ? "true" : undefined}
        className={twMerge(
          // The nav scrolls, which clips a ring drawn outside the row.
          "focus-visible:ring-inset",
          isActive && "bg-gray-50",
          // The rail under the icon fades with the outline's, so the two
          // lines always match.
          "before:transition-opacity before:duration-200 before:ease-out",
          isOutlineOpen ? "starting:before:opacity-0" : "before:opacity-0",
        )}
        // A press toggles the expanded state that every tile shares. Without
        // an outline, it would collapse a heading typed later out of sight.
        onPress={isActive && !hasOutline ? undefined : onPress}
      />
      {hasOutline ? (
        <div
          id={outlineId}
          inert={!isOutlineOpen}
          className={twMerge(
            "grid transition-[grid-template-rows] duration-200 ease-out",
            // Opens from closed when the outline first arrives, which can be a
            // moment after the editor.
            isOutlineOpen
              ? "grid-rows-[1fr] starting:grid-rows-[0fr]"
              : "grid-rows-[0fr]",
          )}
        >
          <div
            className={twMerge(
              "min-h-0 overflow-hidden transition-opacity duration-200 ease-out",
              isOutlineOpen ? "starting:opacity-0" : "opacity-0",
            )}
          >
            <TileOutline
              headings={headings}
              currentHeading={outline?.currentHeading}
              onHeadingPress={onHeadingPress}
            />
          </div>
        </div>
      ) : null}
    </li>
  );
};

interface TileOutlineProps {
  headings: readonly MarkdownOutlineHeading[];
  currentHeading: MarkdownOutlineHeading | undefined;
  onHeadingPress: (lineNumber: number) => void;
}

const TileOutline: FC<TileOutlineProps> = (props) => {
  const { headings, currentHeading, onHeadingPress } = props;
  const listRef = useRef<HTMLOListElement>(null);
  const { rows } = useNavigationRailItems(listRef, headings);
  // By position, since the outline is rebuilt on every edit.
  const currentRow = currentHeading
    ? rows[headings.indexOf(currentHeading)]
    : undefined;

  return (
    <NavigationRail
      listRef={listRef}
      thumb={currentRow ? computeNavigationRailThumb(currentRow) : undefined}
    >
      {headings.map((heading) => {
        const isCurrent = heading === currentHeading;
        return (
          <li key={heading.lineNumber}>
            <NavigationRailItem
              className={
                isCurrent ? "bg-gray-100 hover:bg-gray-100" : undefined
              }
              aria-current={isCurrent ? "location" : undefined}
              onPress={() => onHeadingPress(heading.lineNumber)}
            >
              <span
                className={twMerge(
                  "min-w-0 font-medium break-words",
                  DEPTH_INDENT_CLASSES[heading.depth],
                )}
              >
                {heading.title}
              </span>
            </NavigationRailItem>
          </li>
        );
      })}
    </NavigationRail>
  );
};
